// @givmo/board — the roster page's "Proposed members": the seats the organization's connected AI
// proposed, each waiting for a person to add it or discard it (CBG-AI5-PROPOSE, frame r1; backend
// `board-ai-proposals.md` §6.1).
//
// ══ WHAT THIS CARD MUST NEVER LET SOMEONE MISREAD ════════════════════════════════════════════════
//
//   * A PROPOSAL IS NOT A SEAT (D-CBG-34 reading 1). It sits ABOVE the roster's table, in its own
//     card, never in the table or its count line: nobody here votes, counts toward quorum, gets a
//     packet or can be invited, and the card's one line says they aren't on the board until a person
//     adds them.
//   * ADDING IS THE ROSTER'S OWN ADD. "Review and add" opens "Add a board member", filled from the
//     proposal (`SeatFormDialog`'s `proposal`), and the person's save is `create_member` with the
//     proposal's id: the guarded act (agent-never item 8), the same form, the same words. There is no
//     one-click add, because the person must see what they are adding.
//   * THE AI'S NOTE IS ITS OWN WORDS, labelled as its note, never restated as a fact Board checked,
//     and rendered as plain text (design §6, N4).
//
// Shown only to someone holding `board_member:manage` (the add's own permission); otherwise absent,
// never disabled with a note (Jonathan, 2026-09-28). Absent too when nothing waits.

import { useId } from 'react';
import { Button, Card, CardBody } from '@givmo/design-system';
import { StatusWord } from '../../components/fine-print';
import { displayDate } from '../../lib/display';
import { capacityLineOf, keptTogether } from '../roster/officer-roles';
import { termsWords as tw } from '../roster/terms-words';
import { AiRationale } from './ai-rationale';
import { proposedThrough, type MemberProposal } from './proposal-decode';
import { proposalWords as w } from './proposal-words';

const LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';

/** "Director · Vice Chair and Board Clerk · Ends Mar 10, 2030": the roster's own words for the facts. */
function factsLine(proposal: MemberProposal): string {
  const capacity = capacityLineOf(proposal.is_director, proposal.title_labels ?? []);
  return proposal.term_end_on
    ? `${capacity} · ${keptTogether(tw.rosterEnds(displayDate(proposal.term_end_on)))}`
    : capacity;
}

export function ProposedMembersCard({
  proposals,
  viewerUserId,
  onReview,
  onDiscard,
}: {
  proposals: readonly MemberProposal[];
  viewerUserId: string | null | undefined;
  onReview: (proposal: MemberProposal) => void;
  onDiscard: (proposal: MemberProposal) => void;
}) {
  // Each member's name describes the rationale toggle on its row (CBG-FIX170).
  const nameId = useId();
  const first = proposals[0];
  if (first === undefined) return null;
  return (
    <Card testId="proposed-members">
      <CardBody className="flex flex-col gap-14">
        <div className="flex flex-col gap-4">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            {w.membersHeading}
          </h2>
          <p className={LINE}>{w.membersLead(proposedThrough(first, viewerUserId))}</p>
        </div>
        <ul className="flex flex-col">
          {proposals.map((proposal) => (
            <li
              key={proposal.id}
              className="flex flex-col gap-12 border-t border-border-subtle py-14 last:pb-0 sm:flex-row sm:items-start sm:justify-between"
              data-testid={`proposed-member-${proposal.id}`}
            >
              <div className="flex min-w-0 flex-col gap-2">
                <span
                  id={`${nameId}-${proposal.id}`}
                  className="font-sans text-s14 font-w600 tracking-givmo text-text-strong wrap-anywhere"
                >
                  {proposal.display_name}
                </span>
                {/* CBG-FIX160 I (frame i1): papers often give no address; the person adds one
                    with the seat, so its absence is the card's one coloured word, as a missing
                    file is on the records' page. */}
                {(proposal.email_of_record ?? '').trim().length > 0 ? (
                  <span className="truncate font-sans text-s12 tracking-givmo text-text-tertiary">
                    {proposal.email_of_record}
                  </span>
                ) : (
                  <StatusWord tone="act" testId={`proposed-member-no-email-${proposal.id}`}>
                    {w.noEmailYet}
                  </StatusWord>
                )}
                <span className={`${LINE} pt-4`}>{factsLine(proposal)}</span>
                <AiRationale
                  note={proposal.source_note}
                  testId={`proposed-member-note-${proposal.id}`}
                  describedBy={`${nameId}-${proposal.id}`}
                />
              </div>
              <div className="flex shrink-0 items-center gap-8">
                {/* Quiet acts: the page keeps ONE primary ("Add a member"), and a column of blue
                    buttons would shout louder than the roster it waits above. */}
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={() => onReview(proposal)}
                  testId={`proposed-member-review-${proposal.id}`}
                >
                  {w.reviewAndAdd}
                </Button>
                <Button
                  appearance="saas"
                  tone="ghost"
                  size="sm"
                  onClick={() => onDiscard(proposal)}
                  testId={`proposed-member-discard-${proposal.id}`}
                >
                  {w.discard}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
